/**
 * Hover-to-identify overlay (TEC-465 P3). A reviewer toggles inspector mode on
 * the live site; declaratively-rendered messages (wrapped by <Msg>) get a
 * dashed outline and a native tooltip showing their message id — enough to
 * find the string in the catalogue or source and correct it there. There is
 * no in-place editor: this overlay is look-up only.
 *
 * Renders nothing when inspector mode is not available (non-reviewers can
 * never reach it, even if the flag is forced). Mount once under IntlProvider.
 */
import React, { useEffect } from 'react';
import { useI18nInspector } from './inspector';

/** Shared with StaticInspectorMount.tsx (non-hydrating routes) so the outline/flash rules and
 * class names stay single-sourced across both mount paths. */
export const OVERLAY_STYLE = `
.i18n-inspect-on [data-i18n-inspectable] {
  outline: 1px dashed rgba(59,130,246,0.7);
  outline-offset: 1px;
  cursor: help;
}
.i18n-inspect-on [data-i18n-inspectable]:hover {
  outline: 2px solid rgba(59,130,246,1);
  background: rgba(59,130,246,0.08);
}
.i18n-inspect-flash {
  animation: i18n-inspect-flash 1.6s ease-out;
}
@keyframes i18n-inspect-flash {
  0% { background: rgba(59,130,246,0.35); }
  100% { background: transparent; }
}
`;

export function InspectorOverlay(): React.ReactElement | null {
  const { available, enabled, setEnabled, focusId, setFocusId } = useI18nInspector();

  // Toggle a body class so the marker outline CSS applies only while enabled.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('i18n-inspect-on', enabled);
    return () => document.body.classList.remove('i18n-inspect-on');
  }, [enabled]);

  // Alt+Click a marked string to copy its message id; Shift+Alt+Click copies the
  // string's own rendered text (so a reviewer can paste it straight into
  // extracted/{locale}.json to edit it, without also inheriting the id). A single
  // delegated listener (rather than a per-marker onClick in Msg.tsx) so a bare
  // click always still reaches whatever the marked text is inside (a button, a
  // link, ...) untouched — only Alt+Click (with or without Shift) is intercepted.
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const onClick = (e: MouseEvent): void => {
      if (!e.altKey) return;
      const marker = (e.target as HTMLElement | null)?.closest?.('[data-i18n-inspectable]');
      if (!marker) return;
      const id = marker.getAttribute('data-i18n-id');
      if (!id) return;
      const text = e.shiftKey ? (marker.textContent ?? '').trim() : id;
      if (!text) return;
      e.preventDefault();
      e.stopPropagation();
      navigator.clipboard?.writeText(text).catch(() => {});
      marker.classList.add('i18n-inspect-flash');
      setTimeout(() => marker.classList.remove('i18n-inspect-flash'), 1600);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [enabled]);

  // Deep-link focus: scroll to and briefly flash ?i18nInspect=<id> — the id's
  // tooltip is available from there same as any other marked string.
  useEffect(() => {
    if (!enabled || !focusId) return;
    const marker = document.querySelector(`[data-i18n-id="${CSS.escape(focusId)}"]`) as
      | HTMLElement
      | null;
    if (marker) {
      marker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      marker.classList.add('i18n-inspect-flash');
      setTimeout(() => marker.classList.remove('i18n-inspect-flash'), 1600);
    }
    setFocusId(null);
  }, [enabled, focusId, setFocusId]);

  if (!available) return null;

  return (
    <>
      <style>{OVERLAY_STYLE}</style>

      {/* Floating reviewer control. */}
      <div
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2147483000 }}
        className="rounded-lg bg-jc-elevated shadow-lg ring-1 ring-jc-border p-2 text-xs"
      >
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className={`backoffice-touch rounded px-2 py-1 font-semibold ${
            enabled ? 'bg-blue-600 text-white' : 'bg-jc-page text-jc-ink ring-1 ring-jc-border'
          }`}
        >
          {enabled ? 'Inspector: ON' : 'Inspect translations'}
        </button>
        {enabled && (
          <div className="mt-1 text-jc-ink-secondary">
            Hover a dashed string to see its id · Alt+Click to copy id · Shift+Alt+Click to copy text.
          </div>
        )}
      </div>
    </>
  );
}
